import ApplicationController from "./application_controller"
import Logger from '../lib/logger'

/**
 * Message Composer Controller
 * 
 * A reusable Stimulus controller for message input area with keyboard shortcuts,
 * draft saving, and integration with typing indicator.
 * 
 * Usage:
 *   <form data-controller="message-composer"
 *         data-message-composer-draft-key-value="channel_123"
 *         data-message-composer-typing-indicator-outlet=".typing-indicator"
 *         data-action="turbo:submit-end->message-composer#handleSubmitEnd">
 *     <textarea data-message-composer-target="input"
 *               data-action="keydown->message-composer#handleKeydown 
 *                            input->message-composer#handleInput"></textarea>
 *     <button data-message-composer-target="submit" type="submit">Send</button>
 *   </form>
 */
export default class extends ApplicationController {
  static targets = ["input", "submit", "charCount", "replyContext", "replyToUser", "replyToContent", "form", "parentIdInput", "sendingIndicator"]
  static outlets = ["typing-indicator", "file-preview"]
  static values = {
    draftKey: String,
    maxLength: { type: Number, default: 10000 },
    submitOnEnter: { type: Boolean, default: true },
    saveDrafts: { type: Boolean, default: true },
    draftDebounce: { type: Number, default: 2000 },
    draftId: Number
  }

  connect() {
    super.connect()
    Logger.debug('MessageComposerController connecting', {
      draftKey: this.draftKeyValue,
      maxLength: this.maxLengthValue,
      submitOnEnter: this.submitOnEnterValue
    })

    if (this.hasInputTarget) {
      this.inputTarget.setAttribute('maxlength', this.maxLengthValue)
    }

    this.loadDraft()
    this.updateState()
  }

  disconnect() {
    this.saveDraft()
    clearTimeout(this.draftTimeout)
    clearTimeout(this.typingTimeout)
    super.disconnect()
  }

  // Handle keyboard events
  handleKeydown(event) {
    // Submit on Enter (without Shift)
    if (event.key === "Enter" && !event.shiftKey && this.submitOnEnterValue) {
      event.preventDefault()
      this.submit()
      return
    }

    // Submit on Cmd+Enter / Ctrl+Enter
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      this.submit()
      return
    }

    // Escape to clear
    if (event.key === "Escape") {
      this.clear()
      return
    }
  }

  // Handle input changes
  handleInput() {
    this.updateState()
    this.adjustHeight()
    this.notifyTyping()
    this.scheduleDraftSave()
  }

  // Auto-resize the textarea avoiding CSP inline style blocks via utility classes
  adjustHeight() {
    if (!this.hasInputTarget) return

    const input = this.inputTarget

    // Calculate required rows based on newlines (and crude wrapping factor)
    const lines = input.value.split('\n').length

    // Minimum 1 row, maximum 8 rows (~200px)
    const newRows = Math.min(Math.max(lines, 1), 8)

    // Remove all row classes
    for (let i = 1; i <= 8; i++) {
      input.classList.remove(`rows-${i}`)
    }

    // Add the specific row class
    input.classList.add(`rows-${newRows}`)

    if (lines >= 8) {
      input.classList.add('overflow-y-auto')
    } else {
      input.classList.remove('overflow-y-auto')
    }
  }

  // Update submit button and character count state
  updateState() {
    if (!this.hasInputTarget) return

    const content = this.inputTarget.value.trim()
    const length = this.countCharacters(content)
    const hasFiles = this.hasFilePreviewOutlet && this.filePreviewOutlet.hasFiles
    const isOverLimit = length > this.maxLengthValue

    // Update submit button
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = (length === 0 && !hasFiles) || isOverLimit
    }

    // Update character count
    if (this.hasCharCountTarget) {
      this.charCountTarget.textContent = `${length}/${this.maxLengthValue}`
      
      // Toggle classes for visual feedback
      // We toggle both to ensure the red color is visible (avoiding specificity issues with text-muted)
      this.charCountTarget.classList.toggle("theme-text-danger", isOverLimit)
      this.charCountTarget.classList.toggle("text-danger", isOverLimit)
      this.charCountTarget.classList.toggle("text-muted", !isOverLimit)
    }
  }

  // Accurate character counting including emojis
  countCharacters(text) {
    if (!text) return 0
    try {
      if (typeof Intl !== 'undefined' && Intl.Segmenter) {
        return [...new Intl.Segmenter().segment(text)].length
      }
    } catch (e) {
      Logger.warn('Intl.Segmenter failed, falling back to spread operator', e)
    }
    return [...text].length
  }

  // Submit the form
  submit() {
    if (!this.hasInputTarget) return

    const content = this.inputTarget.value.trim()
    const hasFiles = this.hasFilePreviewOutlet && this.filePreviewOutlet.hasFiles

    if (content.length === 0 && !hasFiles) return
    if (content.length > this.maxLengthValue) return

    // Submit the parent form or child form
    const form = this.element.tagName === "FORM" ? this.element : (this.element.closest("form") || this.element.querySelector("form"))

    if (form) {
      if (typeof form.requestSubmit === "function") {
        form.requestSubmit()
      } else {
        // Fallback for older browsers
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
        form.submit()
      }
    }

    // Stop typing indicator
    this.notifyStoppedTyping()

    // Show sending state
    this.setLoading(true)
  }

  setLoading(isLoading) {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = isLoading
    }
    if (this.hasSendingIndicatorTarget) {
      this.sendingIndicatorTarget.classList.toggle("d-none", !isLoading)
      // Hide the default plane icon if sending
      const planeIcon = this.submitTarget.querySelector(".fa-paper-plane")
      if (planeIcon) planeIcon.classList.toggle("d-none", isLoading)
    }
  }

  // Handle Turbo form submission end
  handleSubmitEnd(event) {
    this.setLoading(false)
    if (event.detail && event.detail.success) {
      Logger.debug('MessageComposerController form submitted successfully')
      this.clear()
      this.clearDraft()
      this.cancelReply()
    } else {
      this.handleError(new Error('Form submission failed'), event.detail)
    }
  }

  // Clear the input
  clear() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.focus()
    }

    // Clear file previews if connected
    if (this.hasFilePreviewOutlet) {
      this.filePreviewOutlet.clear()
    }

    this.updateState()
    this.notifyStoppedTyping()
  }

  // Focus the input
  focus() {
    if (this.hasInputTarget) {
      this.inputTarget.focus()
    }
  }

  // Insert text at cursor position (for emoji picker, etc.)
  insertAtCursor(text) {
    if (!this.hasInputTarget) return

    const input = this.inputTarget
    const start = input.selectionStart
    const end = input.selectionEnd
    const value = input.value

    input.value = value.slice(0, start) + text + value.slice(end)
    input.selectionStart = input.selectionEnd = start + text.length
    input.focus()

    this.updateState()
    this.scheduleDraftSave()
  }

  // === Draft Management ===

  loadDraft() {
    if (!this.saveDraftsValue || !this.hasDraftKeyValue) return

    const draft = localStorage.getItem(this.draftStorageKey)
    if (draft && this.hasInputTarget) {
      this.inputTarget.value = draft
    }
  }

  saveDraft() {
    if (!this.saveDraftsValue || !this.hasDraftKeyValue) return

    const content = this.hasInputTarget ? this.inputTarget.value : ""
    if (content.trim()) {
      localStorage.setItem(this.draftStorageKey, content)
    } else {
      this.clearDraft()
    }
  }

  scheduleDraftSave() {
    clearTimeout(this.draftTimeout)
    this.draftTimeout = setTimeout(() => {
      this.saveDraft()
      this.syncDraftToServer()
    }, this.draftDebounceValue)
  }

  async syncDraftToServer() {
    if (!this.saveDraftsValue || !this.hasInputTarget) return
    const content = this.inputTarget.value.trim()
    if (!content && !this.draftIdValue) return

    const formData = new FormData()
    formData.append('content', content)
    if (this.draftIdValue) formData.append('draft_id', this.draftIdValue)
    
    // Extract context from draftKey (e.g., "channel_123" or "conversation_456")
    if (this.hasDraftKeyValue) {
      const [type, id] = this.draftKeyValue.split('_')
      if (type === 'channel' || type === 'direct') formData.append('channel_id', id)
    }

    try {
      const response = await this.safeFetch('/messages/save_draft', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
      
      if (response && response.ok) {
        const data = await response.json()
        if (data.status === 'success') {
          this.draftIdValue = data.draft_id
          Logger.debug('Draft synced to server', { draft_id: data.draft_id })
        }
      }
    } catch (error) {
      this.handleError(error, { draftId: this.draftIdValue })
    }
  }

  clearDraft() {
    if (this.hasDraftKeyValue) {
      localStorage.removeItem(this.draftStorageKey)
    }
  }

  get draftStorageKey() {
    return `message_draft_${this.draftKeyValue}`
  }

  // === Typing Indicator Integration ===

  notifyTyping() {
    if (!this.hasTypingIndicatorOutlet) return

    // Clear existing stop timeout
    clearTimeout(this.typingTimeout)

    // Send typing notification
    this.typingIndicatorOutlet.sendTyping()

    // Schedule stop notification
    this.typingTimeout = setTimeout(() => {
      this.notifyStoppedTyping()
    }, 3000)
  }

  notifyStoppedTyping() {
    clearTimeout(this.typingTimeout)
    if (this.hasTypingIndicatorOutlet) {
      this.typingIndicatorOutlet.sendStoppedTyping()
    }
  }

  // === Public API for input outlet ===

  // Called by emoji_picker_controller outlet
  insertText(text) {
    this.insertAtCursor(text)
  }

  // Called by emoji-picker:selected event
  handleEmojiSelected(event) {
    if (event.detail && event.detail.emoji) {
      this.insertAtCursor(event.detail.emoji)
    }
  }

  // === Formatting Methods ===

  formatBold() {
    this.wrapSelection("**", "**")
  }

  formatItalic() {
    this.wrapSelection("*", "*")
  }

  formatCode() {
    this.wrapSelection("`", "`")
  }

  wrapSelection(before, after) {
    if (!this.hasInputTarget) return

    const input = this.inputTarget
    const start = input.selectionStart
    const end = input.selectionEnd
    const value = input.value
    const selectedText = value.substring(start, end)

    const newValue = value.substring(0, start) + before + selectedText + after + value.substring(end)
    input.value = newValue

    // Restore focus and selection
    input.focus()
    input.setSelectionRange(start + before.length, end + before.length)

    this.updateState()
    this.scheduleDraftSave()
  }

  // === Reply Management ===


  cancelReply(event) {
    if (event) event.preventDefault()
    
    if (this.hasReplyContextTarget) {
      this.replyContextTarget.classList.add('d-none')
    }
    
    // Clear targets
    if (this.hasReplyToUserTarget) this.replyToUserTarget.textContent = ""
    if (this.hasReplyToContentTarget) this.replyToContentTarget.innerHTML = ""
    if (this.hasParentIdInputTarget) this.parentIdInputTarget.value = ""
  }

  // Get current value (for outlet consumers)
  getValue() {
    return this.hasInputTarget ? this.inputTarget.value : ""
  }
}